import { Link } from 'react-router-dom'
import { ExternalLink, Newspaper } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { maskNuban } from '@/lib/format'
import { CopyableField } from './CopyableField'
import type { AccountSide } from '../types'
import { TierBadge } from '@/features/adverse_media/components/TierBadge'

interface Props {
  side: AccountSide
  /** Header label — "Source" or "Destination". */
  title: string
}

const ACCOUNT_TYPE_LABEL: Record<AccountSide['account_type'], string> = {
  savings: 'Savings',
  current: 'Current',
  corporate: 'Corporate',
  domiciliary: 'Domiciliary',
  wallet: 'Wallet',
}

/**
 * AF-125 — Source / Destination tile.
 *
 * Renders the masked NUBAN, account name, bank short_name (with optional logo),
 * and the account type badge. If the account belongs to an internal customer,
 * a deep link to Customer 360 (AF-131) is rendered alongside the name.
 *
 * Bank logo is opportunistic — falls back to short_name text per technical
 * notes ("Bank logos are optional").
 */
export function SourceDestinationPanel({ side, title }: Props) {
  const { bank, customer_id, customer_number, adverse_media_tier: amTier } = side
  const showAmChip = !!amTier && amTier !== 'clear'

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-card p-4">
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-fg">{title}</h3>
        <div className="flex items-center gap-2">
          {showAmChip && customer_id && (
            <Link
              to={`/customers/${customer_id}`}
              className="inline-flex items-center gap-1 hover:opacity-80"
              title={`Adverse media: ${amTier} — open profile`}
              aria-label={`Adverse media tier ${amTier} — open profile`}
            >
              <Newspaper size={11} className="shrink-0 text-muted-fg" aria-hidden />
              <TierBadge tier={amTier} />
            </Link>
          )}
          {showAmChip && !customer_id && (
            <span
              className="inline-flex items-center gap-1"
              title={`Adverse media: ${amTier}`}
              aria-label={`Adverse media tier ${amTier}`}
            >
              <Newspaper size={11} className="shrink-0 text-muted-fg" aria-hidden />
              <TierBadge tier={amTier} />
            </span>
          )}
          <Badge variant="default">{ACCOUNT_TYPE_LABEL[side.account_type]}</Badge>
        </div>
      </header>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-base font-medium text-fg">
          <span className="truncate">{side.account_name}</span>
          {customer_id && (
            <Link
              to={`/customers/${customer_id}`}
              className="inline-flex shrink-0 items-center gap-1 text-xs text-primary hover:underline"
              aria-label={`Open Customer 360 for ${customer_number ?? customer_id}`}
            >
              {customer_number ?? 'Customer'}
              <ExternalLink size={12} aria-hidden />
            </Link>
          )}
        </div>
        <div className="text-sm">
          <CopyableField
            value={maskNuban(side.nuban)}
            copyLabel={`Copy NUBAN ${maskNuban(side.nuban)}`}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-border pt-3">
        {bank.logo_url ? (
          <img
            src={bank.logo_url}
            alt=""
            className="h-6 w-6 rounded-sm object-contain"
            aria-hidden
          />
        ) : (
          <span
            className="flex h-6 w-6 items-center justify-center rounded-sm bg-muted text-[0.625rem] font-bold text-muted-fg"
            aria-hidden
          >
            {bank.short_name.slice(0, 3)}
          </span>
        )}
        <div className="flex flex-col">
          <span className="text-sm font-medium">{bank.short_name}</span>
          <span className="text-xs text-muted-fg">
            {bank.name} · <span className="font-mono">{bank.cbn_code}</span>
          </span>
        </div>
      </div>
    </div>
  )
}
